import { useAmplitudeExperiment } from '../../core/Tracking/Amplitude/Amplitude';
import {
    Destination,
    isDestinationTypeCountry,
} from '../../resources/destination/domain/types/destination';
import { PickDomain } from '../utilities/types/PickDomain';

const REGION_REDESIGN_EXPERIMENT_KEY = 'cth-iteration-redesign-page-region';
const REGION_REDESIGN_VARIATION = 'variation';

/**
 * Durable cohabitation gate: a region is served the new page when it belongs to the back
 * referential (is migrated). This is the lasting contract — see `docs/product/region-page.md`.
 */
export function isEligibleForNewRegionPage(referentialRegionId: string | null): boolean {
    return referentialRegionId !== null;
}

/**
 * A migrated region is served the new page only for users bucketed into the "variation" variant of the
 * `cth-iteration-redesign-page-region` A/B test. Pure combination — unit-tested without Amplitude.
 */
export function shouldServeNewRegionPage(
    referentialRegionId: string | null,
    variantValue: string | undefined,
): boolean {
    return (
        isEligibleForNewRegionPage(referentialRegionId) &&
        variantValue === REGION_REDESIGN_VARIATION
    );
}

/**
 * A single region is no choice to offer, and the carousel is not designed for one card: the block
 * only earns its place at the top of the page from two regions on.
 */
const MINIMUM_REGION_DISCOVERY_CARDS = 2;

/**
 * Whether this visitor gets the redesigned destination page at all: the "variation" variant on a
 * country destination, however many regions the country has. Country pages only: a desti-région is
 * served the regions of its parent country, which are its siblings — not places to explore within
 * it. Everything the redesign changes hangs off this, the region discovery block included — a
 * country whose regions are too few to fill the block still gets the redesigned page.
 */
function shouldServeRedesignedDestinationPage(
    variantValue: string | undefined,
    isCountryDestination: boolean,
): boolean {
    return isCountryDestination && variantValue === REGION_REDESIGN_VARIATION;
}

/**
 * The destination page serves its region discovery block on a redesigned page, when the country has
 * enough regions to show — whichever resource each comes from. Pure combination — unit-tested
 * without Amplitude.
 */
export function shouldServeRegionDiscoveryBlock(
    cardsCount: number,
    variantValue: string | undefined,
    isCountryDestination: boolean,
): boolean {
    return (
        shouldServeRedesignedDestinationPage(variantValue, isCountryDestination) &&
        cardsCount >= MINIMUM_REGION_DISCOVERY_CARDS
    );
}

/**
 * Decides, client-side, what the destination page renders for this visitor: whether it is the
 * redesigned page at all, and whether that page shows the region discovery block — the two diverge
 * on a country with too few regions to fill the block. Exposes as well whether the A/B variant has
 * resolved yet, which the page-view tracking needs.
 *
 * Client-side on purpose, like the new region page: the destination page is SSR + Varnish-cached
 * and the A/B variant is bucketed per user, so the per-user choice must never be baked into the shared
 * HTML. Server and first client render always show the legacy carousel; the block swaps in once
 * the variant resolves.
 */
export function useDestinationPageDecision(
    cardsCount: number,
    destination: PickDomain<
        Destination,
        'id',
        {
            parentDestination: PickDomain<Destination['parentDestination'], never> | null;
        }
    >,
): { showRegionDiscovery: boolean; isRedesignedPage: boolean; isVariantResolved: boolean } {
    const isCountryDestination = isDestinationTypeCountry(destination);
    const variant = useAmplitudeExperiment(
        REGION_REDESIGN_EXPERIMENT_KEY,
        { destination_id: destination.id.toString() },
        isCountryDestination,
    );

    return {
        showRegionDiscovery: shouldServeRegionDiscoveryBlock(
            cardsCount,
            variant?.value,
            isCountryDestination,
        ),
        isRedesignedPage: shouldServeRedesignedDestinationPage(
            variant?.value,
            isCountryDestination,
        ),
        // "Resolved" once the variant is fetched — or moot, when the destination isn't in the test.
        isVariantResolved: !isCountryDestination || variant !== undefined,
    };
}

/**
 * Decides, client-side, whether to render the new region page — and exposes the two extra signals
 * the page-view tracking needs: whether the region is eligible at all, and whether the A/B variant has
 * resolved yet. Client-side on purpose: the page is SSR + Varnish-cached and the A/B variant is
 * bucketed per user, so a per-user choice must never be baked into the shared server HTML. The
 * variant resolves after hydration (Amplitude fetch), so the server render — and the first client
 * render — always yield the legacy page.
 */
export function useRegionPageDecision(
    referentialRegionId: string | null,
    destinationId: string,
): { shouldServeNewPage: boolean; isVariantResolved: boolean } {
    const isEligible = isEligibleForNewRegionPage(referentialRegionId);
    const variant = useAmplitudeExperiment(
        REGION_REDESIGN_EXPERIMENT_KEY,
        { destination_id: destinationId },
        isEligible,
    );

    return {
        shouldServeNewPage: shouldServeNewRegionPage(referentialRegionId, variant?.value),
        // "Resolved" once the variant is fetched — or moot, when the region isn't in the test.
        isVariantResolved: !isEligible || variant !== undefined,
    };
}
